import { ActionError, Button, WaitPhrase, cn } from '@westhetics/ui'
import { useId, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import { isEmail } from './api'
import { AuthLayout, authInlineLink, authInput, authNote } from './auth-layout'

/**
 * Login (docs/areas/autenticacao.md, "Login" e RN-AUT-02). Formulário em pampas à esquerda, painel
 * em ink à direita (D01). Quem esqueceu a senha entra com código (`/sign-in/code`, RN-AUT-07); a
 * porta de entrada (`/`) decide o que vem depois: troca da senha, escolha de clínica ou o painel.
 */

const MESSAGES = {
  invalid_credentials: 'E-mail ou senha não conferem. Confira os dois e tente de novo.',
  rate_limited: 'Muitas tentativas em pouco tempo. Aguarde um minuto e tente de novo.',
  refused: 'Não foi possível entrar agora. Tente de novo em instantes.',
} as const

export function LoginPage() {
  const session = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const emailId = useId()
  const passwordId = useId()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const expired = new URLSearchParams(location.search).get('reason') === 'expired'
  // A credencial conferiu, mas a API recusou o perfil (conta bloqueada, sem clínica): a causa vem dela.
  const problem = error ?? session.problem
  const canSubmit = isEmail(email) && password.length >= 4

  // Quem já entrou vai para a porta de entrada, que decide o destino pelo perfil e pela implantação.
  if (session.status === 'signed_in') return <Navigate to="/" replace />
  // Senha temporária: a única tela possível é a da troca (RT-04).
  if (session.status === 'password_required') return <Navigate to={routes.newPassword} replace />

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit || submitting) return
    setSubmitting(true)
    setError(null)
    const result = await session.signIn(email.trim().toLowerCase(), password)
    setSubmitting(false)
    if (result.error) {
      setError(result.error.message ?? MESSAGES[result.error.kind])
      return
    }
    navigate('/', { replace: true })
  }

  return (
    <AuthLayout title="Entrar" onSubmit={submit}>
      <label htmlFor={emailId} className="eyebrow mt-8">
        E-mail
      </label>
      <input
        id={emailId}
        type="email"
        autoComplete="username"
        inputMode="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={authInput}
      />

      <div className="mt-6 flex items-baseline justify-between">
        <label htmlFor={passwordId} className="eyebrow">
          Senha
        </label>
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          className="font-body text-eyebrow text-label hover:text-ink cursor-pointer font-light tracking-normal"
        >
          {showPassword ? 'ocultar' : 'mostrar'}
        </button>
      </div>
      <input
        id={passwordId}
        type={showPassword ? 'text' : 'password'}
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className={authInput}
      />

      {problem ? (
        <ActionError className="mt-6" title="Não foi possível entrar" description={problem} />
      ) : null}

      <Button
        type="submit"
        size="lg"
        className="mt-7"
        disabled={!canSubmit}
        loading={submitting}
        loadingLabel="Entrando…"
      >
        Entrar
      </Button>
      {/* A dica diz o que falta enquanto o botão está desabilitado; habilitado, fica só o link. */}
      {expired && !problem ? (
        <p className={cn(authNote, 'text-plum mt-3')}>
          Sua sessão de 12 horas terminou. Entre de novo.
        </p>
      ) : canSubmit ? null : (
        <p className={cn(authNote, 'mt-3')}>Preencha e-mail e senha para entrar.</p>
      )}
      <p className={cn(authNote, 'mt-4')}>
        Esqueceu a senha?{' '}
        <Link to={routes.signInCode} state={{ email: email.trim() }} className={authInlineLink}>
          Entrar com código
        </Link>
      </p>
      <p className={cn(authNote, 'mt-3')}>
        Ainda não usa a Westhetics?{' '}
        <Link to={routes.signup} className={authInlineLink}>
          Criar conta
        </Link>
      </p>
      <WaitPhrase moment="signing_in" active={submitting} className="mt-4" />
    </AuthLayout>
  )
}
